/**
 * Individual Text Field screen (json.admin.individualText).
 *
 * Every element is a box placed by its center:
 * - on the screen (output preview, explanation, each field, Done button): x/y = center in % of the screen width/height,
 *   width in % of the screen width, height in % of the screen height;
 * - text on the output picture: x/y = center in % of the print canvas width/height, width/height in % of the canvas;
 *   the text is aligned inside the box by `align` (left / center / right) and `verticalAlign` (top / center / bottom).
 * The output picture is the Scenes Digital layout when the scenes are on, otherwise the screen of the General tab.
 * Font sizes are px: of the screen (general.width × general.height) or of the output picture;
 * `outputSize` is the output picture size (px) the output font sizes are set for.
 * `version` marks these units; older saved values are converted on load:
 * before 2 positions were px of the Single Photo print canvas, before 3 the output font sizes were px of that canvas.
 */

export const INDIVIDUAL_TEXT_MAX_FIELDS = 3;

export const INDIVIDUAL_TEXT_VERSION = 3;

/** Allowed user input per field: letters and symbols, or digits and what the numeric keypad offers. */
export const INDIVIDUAL_TEXT_INPUT_TYPES = [
    { value: "letters", label: "Letters and symbols" },
    { value: "digits", label: "Digits only" }
];

export const INDIVIDUAL_TEXT_ALIGN_OPTIONS = [
    { value: "left", label: "Left" },
    { value: "center", label: "Center" },
    { value: "right", label: "Right" }
];

export const INDIVIDUAL_TEXT_VERTICAL_ALIGN_OPTIONS = [
    { value: "top", label: "Top" },
    { value: "center", label: "Center" },
    { value: "bottom", label: "Bottom" }
];

const DIGITS_FORBIDDEN = /[^0-9.,+-]/g;
const LETTERS_FORBIDDEN = /[0-9]/g;

/** Removes characters the field's input type does not allow. */
export function sanitizeIndividualText(value, inputType) {
    if (!value) {
        return "";
    }
    return String(value).replace(inputType === "digits" ? DIGITS_FORBIDDEN : LETTERS_FORBIDDEN, "");
}

function getDefaultField(index) {
    const isNumber = index === 2;
    return {
        id: `field${index + 1}`,
        enabled: false,
        // false: the device prints output.text as it is; true: the user can change it
        userEditable: false,
        inputType: isNumber ? "digits" : "letters",
        font: null,
        label: {
            text: ["First name", "Last name", "Number"][index],
            color: "#ffffff",
            fontSize: 40
        },
        // Input background on the device is the label color
        input: {
            textColor: "#000000",
            fontSize: 44
        },
        area: { width: 30, height: 8, x: 50, y: 66 + index * 9 },
        output: {
            // Text set in the cloud, printed on the picture (and prefilled on the device)
            text: "",
            x: 27.5,
            y: 4.5 + index * 5,
            width: 48,
            height: 4.5,
            align: "left",
            verticalAlign: "top",
            fontSize: 64,
            minFontSize: 32,
            color: "#000000"
        }
    };
}

export function getDefaultIndividualText() {
    const fields = [];
    for (let i = 0; i < INDIVIDUAL_TEXT_MAX_FIELDS; i++) {
        fields.push(getDefaultField(i));
    }
    return {
        version: INDIVIDUAL_TEXT_VERSION,
        enabled: false,
        background: null,
        // Width follows the print layout aspect ratio (see start-screen-area aspect-ratio)
        outputPreview: { width: 16, height: 50, x: 50, y: 29 },
        explanation: {
            enabled: false,
            text: "",
            font: null,
            color: "#ffffff",
            fontSize: 44,
            // The text is made smaller down to this size to fit the area
            minFontSize: 24,
            area: { width: 40, height: 4, x: 50, y: 58 }
        },
        fields,
        outputSize: null,
        doneButton: { width: 3.5, height: 6, x: 89, y: 92.5, customIcon: null }
    };
}

function fillDefaults(target, defaults) {
    Object.keys(defaults).forEach((key) => {
        const def = defaults[key];
        if (target[key] === undefined || target[key] === null && def !== null) {
            target[key] = def;
        } else if (def && typeof def === "object" && !Array.isArray(def) && typeof target[key] === "object") {
            fillDefaults(target[key], def);
        }
    });
    return target;
}

/**
 * Converts a screen area saved before version 2 to the center box:
 * "topLeft" areas had x/y at the top left corner; the others had the width in % of the screen height.
 */
function convertScreenArea(area, screenWidth, screenHeight) {
    if (!area) {
        return;
    }
    const width = Number(area.width) || 0;
    const height = Number(area.height) || 0;
    if (area.anchor === "topLeft") {
        area.x = (Number(area.x) || 0) + width / 2;
        area.y = (Number(area.y) || 0) + height / 2;
    } else {
        area.width = width * screenHeight / screenWidth;
    }
    delete area.anchor;
}

/**
 * Converts output text saved before version 2 (px of the print canvas) to the center box in %:
 * "topLeft" had x/y at the box top left corner; older text had x at the alignment anchor of a maxWidth wide line.
 */
function convertOutput(output, canvasWidth, canvasHeight) {
    if (!output) {
        return;
    }
    const fontSize = Number(output.fontSize) || 64;
    let left = Number(output.x) || 0;
    let width = Number(output.width) || 0;
    let height = Number(output.height) || 0;
    if (output.anchor !== "topLeft") {
        width = Number(output.maxWidth) || 600;
        left -= output.align === "center" ? width / 2 : output.align === "right" ? width : 0;
        // The text was placed by its top: a box of one line keeps it there
        height = fontSize;
    }
    output.x = roundTenth((left + width / 2) / canvasWidth * 100);
    output.y = roundTenth(((Number(output.y) || 0) + height / 2) / canvasHeight * 100);
    output.width = roundTenth(width / canvasWidth * 100);
    output.height = roundTenth(height / canvasHeight * 100);
    delete output.anchor;
    delete output.maxWidth;
}

/** Scales the px font sizes of the output text, e.g. when the output picture gets another height. */
export function scaleOutputFont(output, ratio) {
    output.fontSize = Math.max(1, Math.round(Number(output.fontSize) * ratio));
    output.minFontSize = Math.max(1, Math.round(Number(output.minFontSize) * ratio));
}

/**
 * Adds missing properties to a saved config so older configs keep working,
 * and converts values saved before INDIVIDUAL_TEXT_VERSION (screen, print canvas and output picture size in px).
 */
export function normalizeIndividualText(value, screen, canvas, picture) {
    const defaults = getDefaultIndividualText();
    if (!value || typeof value !== "object") {
        return defaults;
    }
    const fields = Array.isArray(value.fields) ? value.fields : [];
    if (!value.version || value.version < 2) {
        const screenWidth = screen && screen.width || 1920;
        const screenHeight = screen && screen.height || 1080;
        convertScreenArea(value.outputPreview, screenWidth, screenHeight);
        convertScreenArea(value.doneButton, screenWidth, screenHeight);
        convertScreenArea(value.explanation && value.explanation.area, screenWidth, screenHeight);
        fields.forEach((field) => {
            if (field) {
                convertScreenArea(field.area, screenWidth, screenHeight);
                convertOutput(field.output, canvas && canvas.width || 1240, canvas && canvas.height || 1844);
            }
        });
        value.version = 2;
    }
    if (value.version === 2) {
        // The font sizes keep their size relative to the picture height
        const canvasHeight = canvas && canvas.height || 1844;
        if (picture && picture.height) {
            fields.forEach((field) => field && field.output && scaleOutputFont(field.output, picture.height / canvasHeight));
        }
        value.version = INDIVIDUAL_TEXT_VERSION;
    }
    fillDefaults(value, defaults);
    value.fields = defaults.fields.map((def, i) => fillDefaults(fields[i] || {}, def));
    // The start-screen-area inputs have a 0.5 step: other values would fail their validation
    const areas = [value.outputPreview, value.explanation.area, value.doneButton].concat(value.fields.map((field) => field.area));
    areas.forEach((area) => ["width", "height", "x", "y"].forEach((key) => {
        area[key] = roundHalf(Number(area[key]) || 0);
    }));
    value.fields.forEach((field) => ["width", "height", "x", "y"].forEach((key) => {
        field.output[key] = roundTenth(Number(field.output[key]) || 0);
    }));
    return value;
}

function clamp(value, min, max) {
    const number = Number(value);
    if (!Number.isFinite(number)) {
        return min;
    }
    return Math.min(Math.max(number, Math.min(min, max)), Math.max(min, max));
}

/** Positions use the 0.5 step of the start-screen-area inputs; other values fail their step validation. */
function roundHalf(value) {
    return Math.round(value * 2) / 2;
}

function roundTenth(value) {
    return Math.round(value * 10) / 10;
}

function floorHalf(value) {
    return Math.floor(value * 2) / 2;
}

/** The output text box in px of the print canvas: {left, top, width, height}. */
export function getOutputBox(output, canvasWidth, canvasHeight) {
    const width = output.width * canvasWidth / 100;
    const height = output.height * canvasHeight / 100;
    return {
        left: output.x * canvasWidth / 100 - width / 2,
        top: output.y * canvasHeight / 100 - height / 2,
        width,
        height
    };
}

/** Moves the output text so its box starts at left/top (px of the print canvas). */
export function moveOutput(output, left, top, canvasWidth, canvasHeight) {
    const box = getOutputBox(output, canvasWidth, canvasHeight);
    output.x = (left + box.width / 2) / canvasWidth * 100;
    output.y = (top + box.height / 2) / canvasHeight * 100;
}

/**
 * Keeps the text box inside the print canvas.
 * changed = "fontSize": a bigger font makes the box higher; otherwise the box height limits the font.
 */
export function clampOutputPosition(output, canvasWidth, canvasHeight, changed) {
    if (!output || !canvasWidth || !canvasHeight) {
        return;
    }
    output.width = roundTenth(clamp(output.width, 0.1, 100));
    if (changed === "fontSize" && Number(output.fontSize) > output.height * canvasHeight / 100) {
        output.height = Math.ceil(output.fontSize / canvasHeight * 1000) / 10;
    }
    output.height = roundTenth(clamp(output.height, 0.1, 100));
    // The text fits the box height
    output.fontSize = Math.round(clamp(output.fontSize, 1, Math.floor(output.height * canvasHeight / 100)));
    output.minFontSize = Math.round(clamp(output.minFontSize, 1, output.fontSize));
    output.x = roundTenth(clamp(output.x, output.width / 2, 100 - output.width / 2));
    output.y = roundTenth(clamp(output.y, output.height / 2, 100 - output.height / 2));
}

/**
 * Keeps a screen element inside the screen, also after the screen size changes.
 * With keepRatio a too big element is scaled down with its proportions.
 */
export function clampScreenArea(area, keepRatio) {
    if (!area) {
        return;
    }
    if (keepRatio && area.width > 0 && area.height > 0) {
        const scale = Math.min(1, 100 / area.width, 100 / area.height);
        area.width *= scale;
        area.height *= scale;
    }
    area.width = floorHalf(clamp(area.width, 0, 100));
    area.height = floorHalf(clamp(area.height, 0, 100));
    // Emptied coordinates go to the center instead of the edge
    ["x", "y"].forEach((key) => {
        if (!Number.isFinite(Number(area[key])) || area[key] === null || area[key] === "") {
            area[key] = 50;
        }
    });
    // Same rounded limits as start-screen-area's validation, so the inputs accept the result
    area.x = clamp(roundHalf(Number(area.x)), roundHalf(area.width / 2), roundHalf(100 - area.width / 2));
    area.y = clamp(roundHalf(Number(area.y)), roundHalf(area.height / 2), roundHalf(100 - area.height / 2));
}

/** A screen element as a box in % of the screen width (left, width) and height (top, height). */
export function getScreenBox(area) {
    return {left: area.x - area.width / 2, top: area.y - area.height / 2, width: area.width, height: area.height};
}

/** Moves a screen element so its box starts at left/top (% of the screen). */
export function moveScreenArea(area, left, top) {
    area.x = left + area.width / 2;
    area.y = top + area.height / 2;
}

/**
 * Moves a dragged box so its start, center or end lands on the closest guide within `threshold`.
 * Returns the new start and the guide it snapped to (null when nothing is close enough).
 */
export function snapToGuides(start, size, guides, threshold) {
    let best = null;
    [0, size / 2, size].forEach((offset) => {
        guides.forEach((guide) => {
            const diff = guide - (start + offset);
            if (Math.abs(diff) <= threshold && (!best || Math.abs(diff) < Math.abs(best.diff))) {
                best = {diff, guide};
            }
        });
    });
    return best ? {start: start + best.diff, guide: best.guide} : {start, guide: null};
}

/** Start, center and end of a box: the lines other boxes snap to. */
export function getBoxGuides(start, size) {
    return [start, start + size / 2, start + size];
}



// WEBPACK FOOTER //
// ./sources/constants/individual-text.constant.js